import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import AdminLayout from '../../components/Layout/AdminLayout';
import {
  Download,
  Search,
  Filter,
  CheckCircle,
  XCircle,
  User,
  Calendar,
  Clock,
  Award,
  FileText,
  Eye,
  Trash2,
  Mail,
  RefreshCw
} from 'lucide-react';
import axios from 'axios';
import toast from 'react-hot-toast';

const ResultsManagement = () => {
  const navigate = useNavigate();
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [sortBy, setSortBy] = useState('completedAt');
  const [sortOrder, setSortOrder] = useState('desc');
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalResults: 0,
    hasNext: false,
    hasPrev: false
  });
  const [pageSize, setPageSize] = useState(20);

  useEffect(() => {
    fetchResults();
  }, [searchTerm, statusFilter, sortBy, sortOrder, pagination.currentPage, pageSize]);

  const fetchResults = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams({
        page: pagination.currentPage.toString(),
        limit: pageSize.toString(),
        search: searchTerm,
        status: statusFilter,
        sortBy: sortBy,
        sortOrder: sortOrder
      });

      const response = await axios.get(`/api/admin/results?${params}`);
      setResults(response.data.results);
      setPagination(response.data.pagination);
    } catch (error) {
      toast.error('Failed to load results');
      console.error('Results fetch error:', error);
    } finally {
      setLoading(false);
    }
  };

  const downloadPDF = async (resultId, studentName) => {
    try {
      const response = await axios.get(`/api/admin/results/${resultId}/pdf`, {
        responseType: 'blob'
      });

      // Create blob URL and download
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `assessment-report-${studentName.replace(/\s+/g, '-')}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      toast.success('PDF report downloaded successfully');
    } catch (error) {
      toast.error('Failed to download PDF report');
      console.error('PDF download error:', error);
    }
  };

  const deleteResult = async (resultId, studentName) => {
    if (!window.confirm(`Are you sure you want to delete the assessment result for ${studentName}? This action cannot be undone.`)) {
      return;
    }

    try {
      await axios.delete(`/api/admin/results/${resultId}`);
      toast.success('Assessment result deleted successfully');

      // If this was the only result on the current page and we're not on page 1, go to previous page
      if (results.length === 1 && pagination.currentPage > 1) {
        setPagination(prev => ({ ...prev, currentPage: prev.currentPage - 1 }));
      } else {
        fetchResults(); // Refresh the current page
      }
    } catch (error) {
      const message = error.response?.data?.message || 'Failed to delete assessment result';
      toast.error(message);
      console.error('Delete result error:', error);
    }
  };

  const resendCertificate = async (resultId, studentName) => {
    if (!window.confirm(`Resend certificate to ${studentName}?`)) {
      return;
    }

    try {
      await axios.post(`/api/admin/results/${resultId}/resend-certificate`);
      toast.success('Certificate resent successfully');
    } catch (error) {
      const message = error.response?.data?.message || 'Failed to resend certificate';
      toast.error(message);
    }
  };

  const getStatusIcon = (passed) => {
    return passed ? (
      <CheckCircle size={16} className="text-success" />
    ) : (
      <XCircle size={16} className="text-danger" />
    );
  };

  const getStatusText = (passed) => {
    return passed ? 'Passed' : 'Failed';
  };

  const getDeliveryStatusDisplay = (mgmtSync) => {
    const status = mgmtSync?.status;
    if (!status) {
      return { icon: null, label: '—', className: 'text-muted' };
    }
    switch (status) {
      case 'delivered':
        return { icon: <CheckCircle size={16} className="text-success" />, label: 'Delivered', className: 'text-success' };
      case 'failed':
        return { icon: <XCircle size={16} className="text-danger" />, label: 'Failed', className: 'text-danger' };
      case 'skipped':
        return { icon: <Clock size={16} className="text-secondary" />, label: 'Not linked', className: 'text-secondary' };
      default:
        return { icon: <Clock size={16} className="text-secondary" />, label: 'Pending', className: 'text-secondary' };
    }
  };

  const retryDelivery = async (resultId) => {
    try {
      const { data } = await axios.post(`/api/admin/results/${resultId}/retry-delivery`);

      if (data && data.delivered) {
        toast.success('Delivered to mgmt');
      } else {
        toast.error(`Delivery did not complete${data && data.reason ? ` (${data.reason})` : ''}`);
      }

      await fetchResults();
    } catch (error) {
      console.error('Retry delivery failed:', error);
      const message = error.response?.data?.message || 'Failed to retry delivery';
      toast.error(message);
    }
  };

  // No more client-side filtering since backend handles it
  const filteredAndSortedResults = results;

  const stats = {
    total: pagination.totalResults,
    passed: results.filter(r => r.passed).length,
    failed: results.filter(r => !r.passed).length,
    averageScore: results.length > 0 ? Math.round(results.reduce((sum, r) => sum + r.percentage, 0) / results.length) : 0
  };

  const handlePageChange = (newPage) => {
    setPagination(prev => ({ ...prev, currentPage: newPage }));
  };

  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
    setPagination(prev => ({ ...prev, currentPage: 1 })); // Reset to first page
  };

  const handleFilterChange = (e) => {
    setStatusFilter(e.target.value);
    setPagination(prev => ({ ...prev, currentPage: 1 })); // Reset to first page
  };

  const handleSortChange = (e) => {
    const [field, order] = e.target.value.split('-');
    setSortBy(field);
    setSortOrder(order);
    setPagination(prev => ({ ...prev, currentPage: 1 })); // Reset to first page
  };

  if (loading) {
    return (
      <AdminLayout title="Results Management">
        <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '400px' }}>
          <div className="loading-spinner"></div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout title="Results Management">
      <div className="results-management">
        {/* Stats Cards */}
        <div className="stats-grid">
          <motion.div
            className="stat-card"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
          >
            <div className="stat-icon total">
              <FileText size={24} />
            </div>
            <div className="stat-content">
              <h3>{stats.total}</h3>
              <p>Total Results</p>
            </div>
          </motion.div>

          <motion.div
            className="stat-card"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
          >
            <div className="stat-icon passed">
              <CheckCircle size={24} />
            </div>
            <div className="stat-content">
              <h3>{stats.passed}</h3>
              <p>Passed</p>
            </div>
          </motion.div>

          <motion.div
            className="stat-card"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
          >
            <div className="stat-icon failed">
              <XCircle size={24} />
            </div>
            <div className="stat-content">
              <h3>{stats.failed}</h3>
              <p>Failed</p>
            </div>
          </motion.div>

          <motion.div
            className="stat-card"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
          >
            <div className="stat-icon average">
              <Award size={24} />
            </div>
            <div className="stat-content">
              <h3>{stats.averageScore}%</h3>
              <p>Average Score</p>
            </div>
          </motion.div>
        </div>

        {/* Filters and Search */}
        <div className="filters-section">
          <div className="search-box">
            <Search size={20} />
            <input
              type="text"
              placeholder="Search by name, email, or assessment code..."
              value={searchTerm}
              onChange={handleSearchChange}
            />
          </div>

          <div className="filter-controls">
            <div className="filter-select">
              <Filter size={16} />
              <select
                value={statusFilter}
                onChange={handleFilterChange}
              >
                <option value="all">All Results</option>
                <option value="passed">Passed Only</option>
                <option value="failed">Failed Only</option>
              </select>
            </div>

            <div className="sort-select">
              <select
                value={`${sortBy}-${sortOrder}`}
                onChange={handleSortChange}
              >
                <option value="completedAt-desc">Latest First</option>
                <option value="completedAt-asc">Oldest First</option>
                <option value="studentName-asc">Name A-Z</option>
                <option value="studentName-desc">Name Z-A</option>
                <option value="score-desc">Highest Score</option>
                <option value="score-asc">Lowest Score</option>
                <option value="assessmentCode-asc">Assessment Code A-Z</option>
                <option value="assessmentCode-desc">Assessment Code Z-A</option>
              </select>
            </div>
          </div>
        </div>

        {/* Results Table */}
        <div className="card">
          <div className="card-header">
            <h2 className="card-title">Assessment Results ({pagination.totalResults} total)</h2>
            <p className="card-subtitle">Complete assessment results and reports</p>
          </div>

          {filteredAndSortedResults.length > 0 ? (
            <div className="table-container">
              <table className="results-table">
                <thead>
                  <tr>
                    <th>Student</th>
                    <th>Status</th>
                    <th>Score</th>
                    <th>Assessment Code</th>
                    <th>Completed</th>
                    <th>Attempt</th>
                    <th>Delivery</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredAndSortedResults.map((result, index) => {
                    const delivery = getDeliveryStatusDisplay(result.mgmtSync);
                    return (
                      <motion.tr
                        key={result._id}
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: index * 0.05 }}
                      >
                      <td>
                        <div className="student-info">
                          <div className="student-avatar">
                            <User size={20} />
                          </div>
                          <div className="student-details">
                            <h4>{result.student.name}</h4>
                            <p>{result.student.email}</p>
                          </div>
                        </div>
                      </td>
                      <td>
                        <div className="status-badge">
                          {getStatusIcon(result.passed)}
                          <span className={result.passed ? 'text-success' : 'text-danger'}>
                            {getStatusText(result.passed)}
                          </span>
                        </div>
                      </td>
                      <td>
                        <div className="score-display">
                          <span className={`score-percentage ${result.passed ? 'passed' : 'failed'}`}>
                            {result.percentage}%
                          </span>
                          <span className="score-points">
                            {result.score}/{result.totalPoints} pts
                          </span>
                        </div>
                      </td>
                      <td>
                        <div className="code-display">
                          <span className="assessment-code">{result.assessmentCode || 'N/A'}</span>
                        </div>
                      </td>
                      <td>
                        <div className="date-display">
                          <Calendar size={16} />
                          <span>{new Date(result.completedAt).toLocaleDateString()}</span>
                          <small>{new Date(result.completedAt).toLocaleTimeString()}</small>
                        </div>
                      </td>
                      <td>
                        <span className="attempt-badge">#{result.attemptNumber}</span>
                      </td>
                      <td>
                        <div className="status-badge">
                          {delivery.icon}
                          <span className={delivery.className}>{delivery.label}</span>
                        </div>
                      </td>
                      <td>
                        <div className="action-buttons">
                          <button
                            className="btn btn-sm btn-primary"
                            onClick={() => navigate(`/admin/results/${result._id}`)}
                            title="View detailed results"
                          >
                            <Eye size={16} />
                          </button>
                          <button
                            className="btn btn-sm btn-outline"
                            onClick={() => downloadPDF(result._id, result.student.name)}
                            title="Download PDF Report"
                          >
                            <Download size={16} />
                          </button>
                          {result.passed && (
                            <button
                              className="btn btn-sm btn-success"
                              onClick={() => resendCertificate(result._id, result.student.name)}
                              title="Resend Certificate"
                            >
                              <Mail size={16} />
                            </button>
                          )}
                          {(result.mgmtSync?.status === 'failed' || result.mgmtSync?.status === 'skipped') && (
                            <button
                              className="btn btn-sm btn-warning"
                              onClick={() => retryDelivery(result._id)}
                              title="Retry delivery"
                            >
                              <RefreshCw size={16} />
                            </button>
                          )}
                          <button
                            className="btn btn-sm btn-danger"
                            onClick={() => deleteResult(result._id, result.student.name)}
                            title="Delete result"
                          >
                            <Trash2 size={16} />
                          </button>
                        </div>
                      </td>
                      </motion.tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="empty-state">
              <FileText size={48} className="text-muted" />
              <h3>No results found</h3>
              <p>
                {searchTerm || statusFilter !== 'all'
                  ? 'Try adjusting your search or filter criteria.'
                  : 'Assessment results will appear here once students complete assessments.'
                }
              </p>
            </div>
          )}
        </div>

        {/* Pagination Controls */}
        {pagination.totalPages > 1 && (
          <div className="pagination-container">
            <div className="pagination-info">
              <span>
                Showing {((pagination.currentPage - 1) * pageSize) + 1} to {Math.min(pagination.currentPage * pageSize, pagination.totalResults)} of {pagination.totalResults} results
              </span>
            </div>

            <div className="pagination-controls">
              <button
                className="btn btn-sm btn-outline"
                onClick={() => handlePageChange(1)}
                disabled={!pagination.hasPrev}
              >
                First
              </button>

              <button
                className="btn btn-sm btn-outline"
                onClick={() => handlePageChange(pagination.currentPage - 1)}
                disabled={!pagination.hasPrev}
              >
                Previous
              </button>

              <div className="page-numbers">
                {Array.from({ length: Math.min(5, pagination.totalPages) }, (_, i) => {
                  const startPage = Math.max(1, pagination.currentPage - 2);
                  const page = startPage + i;
                  if (page > pagination.totalPages) return null;

                  return (
                    <button
                      key={page}
                      className={`btn btn-sm ${page === pagination.currentPage ? 'btn-primary' : 'btn-outline'}`}
                      onClick={() => handlePageChange(page)}
                    >
                      {page}
                    </button>
                  );
                })}
              </div>

              <button
                className="btn btn-sm btn-outline"
                onClick={() => handlePageChange(pagination.currentPage + 1)}
                disabled={!pagination.hasNext}
              >
                Next
              </button>

              <button
                className="btn btn-sm btn-outline"
                onClick={() => handlePageChange(pagination.totalPages)}
                disabled={!pagination.hasNext}
              >
                Last
              </button>
            </div>

            <div className="page-size-selector">
              <label>
                Show:
                <select
                  value={pageSize}
                  onChange={(e) => {
                    setPageSize(parseInt(e.target.value));
                    setPagination(prev => ({ ...prev, currentPage: 1 }));
                  }}
                >
                  <option value="10">10</option>
                  <option value="20">20</option>
                  <option value="50">50</option>
                  <option value="100">100</option>
                </select>
                per page
              </label>
            </div>
          </div>
        )}
      </div>

      <style jsx>{`
        .results-management {
          max-width: 1400px;
          margin: 0 auto;
        }

        .stats-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
          gap: 24px;
          margin-bottom: 32px;
        }

        .stat-card {
          background: var(--white);
          padding: 24px;
          border-radius: var(--border-radius-lg);
          box-shadow: var(--shadow-sm);
          display: flex;
          align-items: center;
          gap: 16px;
        }

        .stat-icon {
          width: 56px;
          height: 56px;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          color: var(--white);
        }

        .stat-icon.total {
          background-color: var(--primary-accent);
        }

        .stat-icon.passed {
          background-color: var(--success);
        }

        .stat-icon.failed {
          background-color: var(--danger);
        }

        .stat-icon.average {
          background-color: var(--info);
        }

        .stat-content h3 {
          font-size: 28px;
          font-weight: 700;
          margin: 0;
          color: var(--text-primary);
        }

        .stat-content p {
          font-size: 14px;
          color: var(--text-secondary);
          margin: 4px 0 0 0;
        }

        .filters-section {
          display: flex;
          gap: 16px;
          margin-bottom: 24px;
          align-items: center;
          flex-wrap: wrap;
        }

        .search-box {
          position: relative;
          flex: 1;
          max-width: 400px;
        }

        .search-box svg {
          position: absolute;
          left: 12px;
          top: 50%;
          transform: translateY(-50%);
          color: var(--text-secondary);
        }

        .search-box input {
          width: 100%;
          padding: 12px 16px 12px 44px;
          border: 2px solid var(--gray-300);
          border-radius: var(--border-radius);
          font-size: 14px;
          transition: var(--transition);
        }

        .search-box input:focus {
          outline: none;
          border-color: var(--primary-accent);
        }

        .filter-controls {
          display: flex;
          gap: 12px;
          align-items: center;
        }

        .filter-select,
        .sort-select {
          position: relative;
          display: flex;
          align-items: center;
          gap: 8px;
        }

        .filter-select svg {
          color: var(--text-secondary);
        }

        .filter-select select,
        .sort-select select {
          padding: 12px 16px;
          border: 2px solid var(--gray-300);
          border-radius: var(--border-radius);
          font-size: 14px;
          background-color: var(--white);
          cursor: pointer;
          transition: var(--transition);
        }

        .filter-select select:focus,
        .sort-select select:focus {
          outline: none;
          border-color: var(--primary-accent);
        }

        .table-container {
          overflow-x: auto;
        }

        .results-table {
          width: 100%;
          border-collapse: collapse;
        }

        .results-table th {
          text-align: left;
          padding: 16px 20px;
          background-color: var(--gray-100);
          color: var(--text-primary);
          font-weight: 600;
          font-size: 14px;
          border-bottom: 1px solid var(--gray-200);
        }

        .results-table td {
          padding: 16px 20px;
          border-bottom: 1px solid var(--gray-200);
          vertical-align: middle;
        }

        .student-info {
          display: flex;
          align-items: center;
          gap: 12px;
        }

        .student-avatar {
          width: 40px;
          height: 40px;
          background-color: var(--primary-bg);
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          color: var(--primary-accent);
        }

        .student-details h4 {
          margin: 0 0 4px 0;
          font-size: 16px;
          font-weight: 600;
          color: var(--text-primary);
        }

        .student-details p {
          margin: 0;
          font-size: 14px;
          color: var(--text-secondary);
        }

        .status-badge {
          display: flex;
          align-items: center;
          gap: 8px;
          font-size: 14px;
          font-weight: 500;
        }

        .score-display {
          display: flex;
          flex-direction: column;
          gap: 4px;
        }

        .score-percentage {
          font-size: 18px;
          font-weight: 700;
        }

        .score-percentage.passed {
          color: var(--success);
        }

        .score-percentage.failed {
          color: var(--danger);
        }

        .score-points {
          font-size: 12px;
          color: var(--text-secondary);
        }

        .code-display {
          text-align: center;
        }

        .assessment-code {
          font-family: 'Courier New', monospace;
          font-size: 14px;
          font-weight: 600;
          color: var(--primary-accent);
          background: var(--primary-bg);
          padding: 4px 8px;
          border-radius: 4px;
          border: 1px solid var(--primary-accent);
        }

        .time-display,
        .date-display {
          display: flex;
          align-items: center;
          gap: 8px;
          font-size: 14px;
          color: var(--text-secondary);
        }

        .date-display {
          flex-direction: column;
          align-items: flex-start;
          gap: 4px;
        }

        .date-display small {
          font-size: 12px;
          opacity: 0.8;
        }

        .attempt-badge {
          background-color: var(--primary-accent);
          color: var(--white);
          padding: 4px 12px;
          border-radius: 12px;
          font-size: 12px;
          font-weight: 600;
        }

        .action-buttons {
          display: flex;
          gap: 8px;
        }

        .empty-state {
          text-align: center;
          padding: 60px 20px;
        }

        .empty-state h3 {
          margin: 16px 0 8px 0;
          color: var(--text-primary);
        }

        .empty-state p {
          color: var(--text-secondary);
          margin: 0;
        }

        .pagination-container {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-top: 24px;
          padding: 20px 24px;
          background: var(--white);
          border-radius: var(--border-radius-lg);
          box-shadow: var(--shadow-sm);
          flex-wrap: wrap;
          gap: 16px;
        }

        .pagination-info {
          font-size: 14px;
          color: var(--text-secondary);
        }

        .pagination-controls {
          display: flex;
          align-items: center;
          gap: 8px;
        }

        .page-numbers {
          display: flex;
          gap: 4px;
        }

        .page-size-selector {
          display: flex;
          align-items: center;
          gap: 8px;
          font-size: 14px;
          color: var(--text-secondary);
        }

        .page-size-selector select {
          padding: 6px 12px;
          border: 1px solid var(--gray-300);
          border-radius: var(--border-radius);
          font-size: 14px;
          background-color: var(--white);
          cursor: pointer;
        }

        .page-size-selector select:focus {
          outline: none;
          border-color: var(--primary-accent);
        }

        @media (max-width: 768px) {
          .stats-grid {
            grid-template-columns: repeat(2, 1fr);
          }

          .filters-section {
            flex-direction: column;
            align-items: stretch;
          }

          .search-box {
            max-width: none;
          }

          .filter-controls {
            flex-direction: column;
            align-items: stretch;
          }

          .results-table th,
          .results-table td {
            padding: 12px 16px;
          }

          .student-info {
            flex-direction: column;
            align-items: flex-start;
            gap: 8px;
          }

          .date-display {
            flex-direction: row;
            align-items: center;
          }

          .pagination-container {
            flex-direction: column;
            align-items: center;
            text-align: center;
          }

          .pagination-controls {
            order: 1;
          }

          .pagination-info {
            order: 2;
            margin-top: 12px;
          }

          .page-size-selector {
            order: 3;
            margin-top: 12px;
          }
        }
      `}</style>
    </AdminLayout>
  );
};

export default ResultsManagement;
